import React, { memo, useEffect } from 'react';
import { Waypoint } from 'react-waypoint';

import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { Box, Table, TableBody } from '@mui/material';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import {
  DEBOUNCE_DELAY_MS,
  DEFAULT_DRAPABLE_ID,
  DEFAULT_TABLE_PLACEHOLDER,
} from './TableContent.constants';
import { bodyStyles, tableStyles } from './TableContent.styles';

const TableContent = ({
  isLoading,
  rowRenderer,
  tableData,
  fetchTableData,
  headerRowRenderer,
  placeholder = DEFAULT_TABLE_PLACEHOLDER,
  onDragEnd = _.noop,
  containerRef,
  isDroppable = false,
  droppableId = DEFAULT_DRAPABLE_ID,
  isEmptyIdPossible = false,
}) => {
  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const isShouldShowPlaceholder = !tableData?.length && !isLoading;

  useEffect(() => {
    if (!containerRef?.current) {
      return;
    }

    const { current } = containerRef;
    const throttledTooltipReset = _.throttle(onResetTooltipState, DEBOUNCE_DELAY_MS);

    current.addEventListener('scroll', throttledTooltipReset);

    return () => {
      current.removeEventListener('scroll', throttledTooltipReset);
    };
  }, [containerRef, onResetTooltipState]);

  const tableBodyContent = (
    <>
      {tableData.map((item, index) => {
        const itemWithId =
          !_.isObject(item) || item.id || isEmptyIdPossible
            ? item
            : { ...item, id: _.uniqueId('temp-') };

        return rowRenderer(itemWithId, index);
      })}

      {!isLoading && !!containerRef?.current && (
        <Waypoint scrollableAncestor={containerRef.current} onEnter={fetchTableData}>
          <tr className="absolute w-full h-200 bottom-0 -z-10" />
        </Waypoint>
      )}
    </>
  );

  return (
    <Box className="relative h-100" onMouseLeave={onResetTooltipState} onMouseOver={onMouseOver}>
      <TruncatedProvider />
      <Table stickyHeader sx={tableStyles}>
        {headerRowRenderer?.()}

        {!isShouldShowPlaceholder && isDroppable ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable direction="vertical" droppableId={droppableId}>
              {provided => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef} sx={bodyStyles}>
                  {tableBodyContent}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <TableBody sx={bodyStyles}>{tableBodyContent}</TableBody>
        )}
      </Table>
      {isShouldShowPlaceholder && <NoAvailableMessage message={placeholder} />}
    </Box>
  );
};

TableContent.propTypes = {
  ...WithLoadingPropTypes,
  rowRenderer: PropTypes.func.isRequired,
  tableData: PropTypes.array.isRequired,
  containerRef: PropTypes.object,
  droppableId: PropTypes.string,
  headerRowRenderer: PropTypes.func,
  isEmptyIdPossible: PropTypes.bool,
  isDroppable: PropTypes.bool,
  placeholder: PropTypes.string,
  onDragEnd: PropTypes.func,
};

export default memo(TableContent);
